import { Col, Form, Input, Row } from 'antd';
import { type NamePath } from 'antd/lib/form/interface';
import classnames from 'classnames';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { validatePasswordConditionSeparatly } from 'common/validators';
import { Text } from '../';
import { CheckPasswordIcon, ClosepasswordIcon, EyeCloseIcon, EyeOpenIcon } from '../Icons';

type ItemP = Omit<React.ComponentProps<typeof Form.Item>, 'children' | 'label'> & {
  label?: string;
};

type InputP = React.ComponentProps<typeof Input.Password>;

type P = {
  item: ItemP;
  input?: InputP;
  showValidation?: boolean;
};

const ValidationInfo = ({
  valid,
  showErrorState,
  title,
}: {
  valid: boolean;
  showErrorState: boolean;
  title: string;
}) => {
  const { t } = useTranslation();

  return (
    <li
      className={classnames({
        // TODO this is too jumping in UI
        'password-input__validated': showErrorState || valid,
      })}
    >
      <Text
        item={{
          className: 'password-input__text',
          type: showErrorState || valid ? (valid ? 'success' : 'danger') : 'secondary',
        }}
      >
        {showErrorState || valid ? valid ? <CheckPasswordIcon /> : <ClosepasswordIcon /> : null}
        {t(title)}
      </Text>
    </li>
  );
};

const PasswordValidationInfo = ({ name }: { name?: NamePath }) => {
  if (!name) {
    return null;
  }

  return (
    <Form.Item shouldUpdate>
      {({ getFieldValue, getFieldError }) => {
        const password: string | undefined = getFieldValue(name);

        const hasValidationErrors = Boolean(password && getFieldError(name)?.length > 0);
        const passwordCheck = validatePasswordConditionSeparatly(password ?? '');

        return (
          <Row className="password-input__validation">
            <Col span={12}>
              <ul>
                <ValidationInfo
                  valid={passwordCheck.lowercase}
                  showErrorState={hasValidationErrors}
                  title="Lowercase letter"
                />
                <ValidationInfo valid={passwordCheck.number} showErrorState={hasValidationErrors} title="Number" />
                <ValidationInfo
                  valid={passwordCheck.longEnough}
                  showErrorState={hasValidationErrors}
                  title="At least 12 characters"
                />
              </ul>
            </Col>
            <Col span={12}>
              <ul>
                <ValidationInfo
                  valid={passwordCheck.uppercase}
                  showErrorState={hasValidationErrors}
                  title="Uppercase letter"
                />
                <ValidationInfo
                  valid={passwordCheck.special}
                  showErrorState={hasValidationErrors}
                  title="Symbol: @#$%^&* etc."
                />
              </ul>
            </Col>
          </Row>
        );
      }}
    </Form.Item>
  );
};

const PasswordInput = ({ item, input, showValidation }: P) => {
  const { t } = useTranslation();

  return (
    <>
      <Form.Item {...item} label={item.label ? t(item.label) : item.label} required={false}>
        <Input.Password
          {...input}
          size="large"
          iconRender={(visible) => (visible ? <EyeOpenIcon cursor="pointer" /> : <EyeCloseIcon cursor="pointer" />)}
        />
      </Form.Item>

      {showValidation ? <PasswordValidationInfo name={item?.name} /> : null}
    </>
  );
};

export default PasswordInput;
